/**
 * useBroadcastSession - the brain of the ReplayKit live-scan pipeline.
 *
 * Subscribes to the BroadcastBridge plugin, manages the UI state machine for
 * the broadcast pill, and drives the frame-upload loop end-to-end.
 *
 * State transitions:
 *
 *      [idle]
 *        |
 *        |  trainer taps pill -> requestNotificationPermission()
 *        v
 *   [requesting-permission]
 *        |               \
 *        |  granted       \  denied
 *        v                 v
 *   [waiting-to-broadcast]  [permission-denied]
 *        |                       |
 *        |  iOS picker reports   |  user taps "Enable in Settings"
 *        |  status == recording  |  -> openSettings()
 *        v                       |
 *      [live]                    +--> [idle] (after they come back)
 *        |   (frame loop runs)
 *        |   on bridge status == finished
 *        v
 *   [processing]
 *        |
 *        |   drain remaining frames, then
 *        v
 *      [idle]  (with completion toast / notification)
 *
 *   any path -> [error] on irrecoverable failure, with retry tap back to [idle].
 *
 * Frame upload loop posts each consumed frame to /api/dex/screenshot-batch
 * exactly the same shape the existing Box Scan path uses (audit confirms
 * the endpoint accepts our shape and dedupes via SHA-256 on contentHash).
 * The endpoint validates Pokemon records; we treat the JPEG as evidence to
 * be OCR'd server-side via the spec'd review path. For B4 we treat each
 * frame as a "frame I captured, here's the bytes" sample and route through
 * the same content-hash dedup.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  broadcastBridge,
  type BroadcastStatus,
  type BroadcastFrameMeta,
} from "@/lib/broadcast-bridge";
import {
  requestNotificationPermission,
  getCachedNotificationPermission,
  type PermissionState,
} from "@/lib/notifications";
import { apiRequestRaw } from "@/lib/queryClient";
import { isNative } from "@/lib/platform";

export type SessionState =
  | "idle"
  | "requesting-permission"
  | "permission-denied"
  | "waiting-to-broadcast"
  | "live"
  | "processing"
  | "error";

export interface BroadcastSessionStats {
  state: SessionState;
  framesCaptured: number;
  speciesMatched: number;
  /** True while a frame is being POSTed. Used to surface "uploading" hint. */
  uploading: boolean;
  /** Set when state is "error"; cleared on retry. */
  errorMessage: string | null;
  /** Permission state at the moment of the last read. */
  permission: PermissionState;
}

const STATUS_POLL_MS = 2000;
const FRAME_LOOP_INTERVAL_MS = 1000;

async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function useBroadcastSession() {
  const queryClient = useQueryClient();

  const [state, setState] = useState<SessionState>("idle");
  const [framesCaptured, setFramesCaptured] = useState(0);
  const [speciesMatched, setSpeciesMatched] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [permission, setPermission] = useState<PermissionState>("unknown");

  // Mutable handles for timers / pumps so the cleanup effect can clear them.
  const statusTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const frameTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Latest known broadcast status. We use a ref so the frame pump can read
  // the freshest value without re-running the effect on every status tick.
  const lastStatusRef = useRef<BroadcastStatus>({ state: "idle" });
  // Guard so we never spawn two parallel frame pumps.
  const pumpRunningRef = useRef(false);
  // Track whether the trainer started this session so the bridge's lingering
  // status.json from a prior recording doesn't auto-arm us.
  const sessionActiveRef = useRef(false);

  // ── Initial permission read (no prompt) ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    getCachedNotificationPermission().then((p) => {
      if (!cancelled) setPermission(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Status polling ──────────────────────────────────────────────────────
  // Runs every 2 seconds while a session is active. Reads status.json the
  // SampleHandler writes. Transitions state based on what's there.

  const pollStatus = useCallback(async () => {
    const status = await broadcastBridge.getStatus();
    lastStatusRef.current = status;

    // Only react to bridge state when the trainer actually started a session
    // from our UI. A leftover status.json from a previous broadcast would
    // otherwise auto-arm the upload pump on app open.
    if (!sessionActiveRef.current) return;

    if (status.state === "recording" && state !== "live") {
      setState("live");
      // No banner here (2026-09-11): the trainer is already scanning.
    } else if (status.state === "finished" && state === "live") {
      setState("processing");
    }
  }, [state]);

  // ── Frame upload pump ───────────────────────────────────────────────────
  // Runs once per second while state is "live" OR "processing" (the
  // processing phase drains any frames the extension wrote after the
  // trainer tapped Stop but before iOS torn down the upload process).

  const drainFrames = useCallback(async () => {
    if (pumpRunningRef.current) return;
    pumpRunningRef.current = true;
    try {
      const frames: BroadcastFrameMeta[] = await broadcastBridge.listFrames();
      if (frames.length === 0) {
        // Processing -> idle transition: if the bridge says finished AND
        // there are no frames left, we're done.
        if (
          state === "processing" &&
          lastStatusRef.current.state === "finished"
        ) {
          finalize();
        }
        return;
      }
      setUploading(true);
      for (const frame of frames) {
        const consumed = await broadcastBridge.consumeFrame(frame.path);
        if (!consumed) continue;
        const newMatches = await postFrame(consumed.base64, frame.timestamp);
        setFramesCaptured((n) => n + 1);
        if (newMatches > 0) {
          setSpeciesMatched((n) => n + newMatches);
          // Optimistic cache invalidation so the Dex completion bar updates
          // without waiting for a refetch interval to elapse.
          queryClient.invalidateQueries({ queryKey: ["/api/dex/completion"] });
          queryClient.invalidateQueries({ queryKey: ["/api/dex/batches"] });
        }
      }
    } catch (err: any) {
      setErrorMessage(err?.message ?? "Frame upload failed.");
      setState("error");
    } finally {
      setUploading(false);
      pumpRunningRef.current = false;
    }
    // We intentionally read state via closure here; drainFrames re-evaluates
    // every frame interval so the latest state is in scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, queryClient]);

  // ── Finalize ────────────────────────────────────────────────────────────

  const finalize = useCallback(() => {
    const totalFrames = framesCaptured;
    const totalSpecies = speciesMatched;
    sessionActiveRef.current = false;
    setState("idle");
    // No "Scan complete" banner (2026-09-11): the results are on screen.
    // Final cache invalidation so the Dex page lands on truth on next open.
    queryClient.invalidateQueries({ queryKey: ["/api/dex/completion"] });
    queryClient.invalidateQueries({ queryKey: ["/api/dex/batches"] });
  }, [framesCaptured, speciesMatched, queryClient]);

  // ── Timer wiring ────────────────────────────────────────────────────────

  useEffect(() => {
    // Status poll: always on while mounted AND not in a terminal state.
    if (state === "idle" || state === "error" || state === "permission-denied") {
      if (statusTimerRef.current) {
        clearInterval(statusTimerRef.current);
        statusTimerRef.current = null;
      }
    } else if (!statusTimerRef.current) {
      statusTimerRef.current = setInterval(pollStatus, STATUS_POLL_MS);
    }

    // Frame loop: only while live or processing.
    if (state === "live" || state === "processing") {
      if (!frameTimerRef.current) {
        frameTimerRef.current = setInterval(drainFrames, FRAME_LOOP_INTERVAL_MS);
      }
    } else if (frameTimerRef.current) {
      clearInterval(frameTimerRef.current);
      frameTimerRef.current = null;
    }

    return () => {
      // Cleanup on unmount. Idle / error / permission-denied transitions
      // also reach here on the next pass.
      if (statusTimerRef.current && (state === "idle" || state === "error")) {
        clearInterval(statusTimerRef.current);
        statusTimerRef.current = null;
      }
    };
  }, [state, pollStatus, drainFrames]);

  // ── Final unmount cleanup ──────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (statusTimerRef.current) clearInterval(statusTimerRef.current);
      if (frameTimerRef.current) clearInterval(frameTimerRef.current);
    };
  }, []);

  // ── Public actions ─────────────────────────────────────────────────────

  const start = useCallback(async () => {
    setErrorMessage(null);
    if (!isNative()) {
      // Web cannot broadcast. Surface a friendly error.
      setErrorMessage("Live Scan works on the iOS app. Open Nexus Dex on your phone to start.");
      setState("error");
      return;
    }
    setState("requesting-permission");
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === "denied") {
      setState("permission-denied");
      return;
    }
    // Reset counters at the START of every new session.
    setFramesCaptured(0);
    setSpeciesMatched(0);
    // Wipe any stale frames the extension may have left in the App Group
    // container from a previous broadcast. Best-effort.
    await broadcastBridge.clearFrames();
    sessionActiveRef.current = true;
    setState("waiting-to-broadcast");
    // The actual broadcast-picker tap is owned by BroadcastAutoStart (the
    // existing plugin) and Inventory.tsx will call it after this hook
    // armed the session.
  }, []);

  const cancel = useCallback(async () => {
    sessionActiveRef.current = false;
    await broadcastBridge.clearFrames();
    setState("idle");
    setFramesCaptured(0);
    setSpeciesMatched(0);
    setErrorMessage(null);
  }, []);

  const retry = useCallback(() => {
    setErrorMessage(null);
    setState("idle");
  }, []);

  // ── Memoised stats ─────────────────────────────────────────────────────

  const stats: BroadcastSessionStats = useMemo(
    () => ({
      state,
      framesCaptured,
      speciesMatched,
      uploading,
      errorMessage,
      permission,
    }),
    [state, framesCaptured, speciesMatched, uploading, errorMessage, permission],
  );

  return { stats, start, cancel, retry };
}

// ── Upload helper ─────────────────────────────────────────────────────────
// Posts a single base64 JPEG to /api/dex/screenshot-batch using the same
// contract the Box Scan path uses (audit-verified). Returns the count of
// new species matched in the server's response, or 0 on dedup / failure.

async function postFrame(base64: string, timestampMs: number): Promise<number> {
  try {
    // Build a stable contentHash from the bytes so re-uploading the same
    // frame (network retry, app foregrounded mid-pump) dedupes server-side.
    const sample = base64.slice(0, 64) + ":" + base64.length + ":" + timestampMs;
    const contentHash = await sha256Hex(sample);
    const res = await apiRequestRaw("POST", "/api/dex/screenshot-batch", {
      source: "live_scan",
      surface: "mobile",
      contentHash,
      // For B4 we treat the JPEG as a captured screen; the server-side
      // OCR / review pipeline matches species and writes records. Until the
      // server understands a raw `frameBase64` payload, we send the frame
      // metadata in `reviewQueueRows` so the trainer's review queue gets a
      // row per frame; confirmedRows stays empty until OCR fires.
      confirmedRows: [],
      reviewQueueRows: [
        {
          // Species not yet known; the server's OCR pass fills these in.
          // We pass the frame bytes for the review queue to OCR offline.
          spriteDataUrl: `data:image/jpeg;base64,${base64}`,
          ocrWarnings: ["broadcast_frame"],
          confidence: 0,
        },
      ],
    });
    if (!res.ok) return 0;
    const body = (await res.json().catch(() => ({}))) as { accepted?: number };
    return Number(body?.accepted ?? 0);
  } catch {
    return 0;
  }
}
